<script>
import {gsap} from "gsap";
import {PhChatCircleDots, PhCheck, PhClipboardText, PhCheckCircle, PhGlobeHemisphereWest, PhInstagramLogo, PhLinkedinLogo, PhSparkle, PhTarget, PhXLogo} from "@phosphor-icons/vue";

export default {
    components: {
        PhChatCircleDots,
        PhCheck,
        PhTarget,
        PhClipboardText,
        PhCheckCircle,
        PhGlobeHemisphereWest,
        PhInstagramLogo,
        PhXLogo,
        PhLinkedinLogo,
        PhSparkle,
    },

    data() {
        return {
            // HERO CHAT
            heroMsgIdx: 0,
            heroMessages: [
                {type: "user", text: "How much is 8 weeks IELTS preparation?"},
                {
                    type: "bot",
                    text: "8-week IELTS Prep is <strong>CAD 3,440</strong> + CAD 225 enrollment fee. September and October start dates available. Want to know about accommodation too?",
                },
                {type: "user", text: "Yes! And can I apply online?"},
                {
                    type: "bot",
                    text: "Homestay from CAD 1,200/month or student residence from CAD 900/month. And yes — apply 100% online. Shall I send you the application link? 📩",
                },
            ],

            // COUNTER
            countersStarted: false,

            whoCards: [
                {
                    id: "language",
                    cardClass: "wcard-s",
                    tagClass: "wc-tag-s",
                    dotClass: "dot-s",
                    tag: "Language Schools",
                    title: "Convert more visitors into enrolled students",
                    sub: "Your chatbot knows your exact programs, pricing and availability — always live because it reads directly from your GDS.",
                    items: [
                        "Chatbot synced with your GDS — prices always accurate",
                        "Upload a PDF price list → AI fills your GDS in minutes",
                        "Every lead scored, assigned, and tracked to enrollment",
                        "Analytics: which countries convert, which programs sell",
                    ],
                },
                {
                    id: "recruiters",
                    cardClass: "wcard-a",
                    tagClass: "wc-tag-a",
                    dotClass: "dot-a",
                    tag: "Education Recruiters",
                    title: "Place more students with less manual work",
                    sub: "AI qualifies your leads, matches them to programs, and tracks every application from inquiry to enrollment confirmation.",
                    items: [
                        "AI qualifies students: budget, level, destination, start date",
                        "Live pricing from 500+ schools in one platform",
                        "Send applications through AMS — track every status update",
                        "Student panel: students track their own application live",
                    ],
                },
                {
                    id: "academic",
                    cardClass: "wcard-c",
                    tagClass: "wc-tag-c",
                    dotClass: "dot-c",
                    tag: "Academic Schools",
                    title: "Turn your website and documents into a live enrollment engine",
                    sub: "Your chatbot learns from your uploaded brochures, course PDFs, and website — always reflecting the latest programs, fees, and intake dates.",
                    items: [
                        "Upload brochures and PDFs — AI fills your knowledge base instantly",
                        "Website analysis keeps program info accurate without manual updates",
                        "Every enquiry scored, routed, and tracked to enrolment",
                        "Analytics: which sources convert, which courses attract most interest",
                    ],
                },
                {
                    id: "providers",
                    cardClass: "wcard-p",
                    tagClass: "wc-tag-p",
                    dotClass: "dot-p",
                    tag: "Providers",
                    title: "Showcase your offering and connect with the right partners",
                    sub: "Make your programs discoverable to recruiters and schools worldwide. Keep your availability, pricing, and requirements always up to date — no back-and-forth emails needed.",
                    items: [
                        "List programs, availability, and pricing in one central profile",
                        "Receive qualified leads matched to your intake criteria",
                        "Track applications and confirmations in real time",
                        "Integrate with your existing tools via API or document upload",
                    ],
                },
            ],

            // CALCULATOR
            visitors: 10000,
            avgRevenue: 4500,

            // DEMO CHAT
            demoChatOpen: false,
            demoMsgCount: 0,
            leadFormShown: false,

            demoResponses: {
                price: "Great question! Prices vary by program and duration. For example, our <strong>IELTS Preparation</strong> (8 weeks) is CAD 3,440.",

                ielts: "Our <strong>IELTS Preparation program</strong> runs 4–12 weeks.",

                general: "<strong>General English</strong> is our most popular program!",

                business: "<strong>Business English Plus</strong> is designed for working professionals.",

                apply: "Applying is straightforward! You can apply <strong>100% online</strong>.",

                visa: "For visa guidance, it depends on your nationality and destination country.",

                accom: "We offer two accommodation options: <strong>Homestay</strong> and <strong>Student Residence</strong>.",

                start: "Classes start <strong>every Monday</strong> year-round!",

                gds: "The <strong>Global Distribution System (GDS)</strong> connects your schools and programs.",

                demo: "I'd love to show you a full demo!",

                default: "That's a great question! I'm the AMS AI demo assistant.",
            },
        };
    },

    computed: {
        calcData() {
            const convs = Math.round(this.visitors * 0.1);
            const leads = Math.round(convs * 0.28);
            const enr = Math.round(leads * 0.16);
            const rev = enr * this.avgRevenue;
            const missed = Math.round(this.visitors * 0.9);

            return {
                convs,
                leads,
                enr,
                rev,
                missed,
            };
        },
    },

    mounted() {
        this.runHeroChat();
        this.initCounters();
        this.initLeadCards();
        this.initWhiteGloveAnimation();

        setTimeout(() => {
            const tooltip = this.getEl("demo-tooltip");
            if (tooltip) tooltip.style.display = "none";
        }, 6000);
    },

    methods: {
        getEl(id) {
            return this.$el?.querySelector(`#${id}`) ?? null;
        },

        scrollToCta() {
            this.$el?.querySelector(".cta-section")?.scrollIntoView({behavior: "smooth"});
        },

        formatCurrency(value) {
            return `$${Math.round(value).toLocaleString()}`;
        },

        // ── HERO CHAT ──────────────────────────────────
        runHeroChat() {
            setTimeout(this.addHeroMsg, 1500);
        },

        addHeroMsg() {
            const heroChat = this.getEl("hero-chat");

            if (!heroChat) return;

            if (this.heroMsgIdx >= this.heroMessages.length) {
                setTimeout(() => {
                    heroChat.innerHTML = `
            <div class="chat-bot">
              <div class="chat-av">O</div>
              <div class="chat-bub">
                Hi! I'm ABC School's AI assistant.
              </div>
            </div>
          `;

                    this.heroMsgIdx = 0;

                    setTimeout(this.runHeroChat, 2000);
                }, 4000);

                return;
            }

            const msg = this.heroMessages[this.heroMsgIdx];

            if (msg.type === "bot") {
                const el = document.createElement("div");

                el.className = "chat-bot";

                el.innerHTML = `
          <div class="chat-av">O</div>
          <div class="typing">
            <span></span><span></span><span></span>
          </div>
        `;

                heroChat.appendChild(el);

                setTimeout(() => {
                    el.innerHTML = `
            <div class="chat-av">O</div>
            <div class="chat-bub">${msg.text}</div>
          `;

                    this.heroMsgIdx++;

                    setTimeout(this.addHeroMsg, 1800);
                }, 1200);
            } else {
                const el = document.createElement("div");

                el.className = "chat-user";

                el.innerHTML = `
          <div class="chat-ubub">${msg.text}</div>
        `;

                heroChat.appendChild(el);

                this.heroMsgIdx++;

                setTimeout(this.addHeroMsg, 800);
            }
        },

        // ── LEAD CARDS ─────────────────────────────────
        initLeadCards() {
            gsap.to(["#lc1", "#lc2", "#lc3"], {
                opacity: 1,
                x: 0,
                duration: 0.45,
                ease: "power2.out",
                stagger: 0.22,
                delay: 0.6,
            });
        },

        // ── COUNTERS ───────────────────────────────────
        initCounters() {
            const statsBar = this.$el?.querySelector(".stats-bar");

            if (!statsBar) return;

            const statsObs = new IntersectionObserver(
                (entries) => {
                    if (entries[0].isIntersecting && !this.countersStarted) {
                        this.countersStarted = true;

                        this.$el?.querySelectorAll(".counter").forEach((el) => {
                            const target = parseInt(el.dataset.target);

                            let curr = 0;
                            let frame = 0;

                            const step = target / 60;

                            const t = setInterval(() => {
                                frame++;

                                curr = Math.min(Math.round(step * frame), target);

                                el.textContent = curr.toLocaleString();

                                if (curr >= target) {
                                    clearInterval(t);
                                }
                            }, 18);
                        });
                    }
                },
                {threshold: 0.5},
            );

            statsObs.observe(statsBar);
        },

        // ── SUBMIT ─────────────────────────────────────
        handleSubmit(btn) {
            btn.innerHTML = "✓ &nbsp;Request received — we'll be in touch!";

            btn.style.background = "var(--green)";
            btn.disabled = true;
        },

        // ── DEMO CHAT ──────────────────────────────────
        getDemoResponse(msg) {
            const m = msg.toLowerCase();

            if (m.match(/price|cost|fee|how much|expensive|cheap/)) {
                return this.demoResponses.price;
            }

            if (m.match(/ielts|toefl|cambridge/)) {
                return this.demoResponses.ielts;
            }

            if (m.match(/general english|basic|beginner/)) {
                return this.demoResponses.general;
            }

            if (m.match(/business|professional|work/)) {
                return this.demoResponses.business;
            }

            if (m.match(/apply|application|register/)) {
                return this.demoResponses.apply;
            }

            if (m.match(/visa|study permit/)) {
                return this.demoResponses.visa;
            }

            if (m.match(/accommodation|housing|homestay/)) {
                return this.demoResponses.accom;
            }

            if (m.match(/start|date|intake/)) {
                return this.demoResponses.start;
            }

            if (m.match(/gds|platform|system/)) {
                return this.demoResponses.gds;
            }

            if (m.match(/demo|chatbot|ams ai/)) {
                return this.demoResponses.demo;
            }

            return this.demoResponses.default;
        },

        addDemoMsg(type, text, isTyping = false) {
            const msgs = this.getEl("demo-msgs");

            if (!msgs) return;

            if (type === "bot") {
                if (isTyping) {
                    const t = document.createElement("div");

                    t.className = "dm-bot";
                    t.id = "dm-typing";

                    t.innerHTML = `
            <div class="dm-av">A</div>
            <div class="dm-typing">
              <span></span><span></span><span></span>
            </div>
          `;

                    msgs.appendChild(t);
                    msgs.scrollTop = msgs.scrollHeight;

                    return;
                }

                this.getEl("dm-typing")?.remove();

                const el = document.createElement("div");

                el.className = "dm-bot";

                el.innerHTML = `
          <div class="dm-av">A</div>
          <div class="dm-bub">${text}</div>
        `;

                msgs.appendChild(el);
            } else {
                const el = document.createElement("div");

                el.className = "dm-user";

                el.innerHTML = `
          <div class="dm-ubub">${text}</div>
        `;

                msgs.appendChild(el);
            }

            msgs.scrollTop = msgs.scrollHeight;
        },

        sendDemo() {
            const input = this.getEl("demo-input");

            if (!input) return;

            const msg = input.value.trim();

            if (!msg) return;

            input.value = "";

            this.addDemoMsg("user", msg);

            this.demoMsgCount++;

            this.addDemoMsg("bot", "", true);

            setTimeout(() => {
                this.addDemoMsg("bot", this.getDemoResponse(msg));

                if (this.demoMsgCount >= 2 && !this.leadFormShown) {
                    this.leadFormShown = true;

                    setTimeout(() => {
                        const leadForm = this.getEl("demo-lead-form");

                        if (leadForm) leadForm.style.display = "block";
                    }, 800);
                }
            }, 1200);
        },

        openDemo() {
            const panel = this.getEl("demo-panel");
            const tooltip = this.getEl("demo-tooltip");

            if (!panel) return;

            panel.classList.add("open");

            this.demoChatOpen = true;

            if (tooltip) tooltip.style.display = "none";

            const msgs = this.getEl("demo-msgs");

            if (msgs && msgs.children.length === 0) {
                this.addDemoMsg("bot", "", true);

                setTimeout(() => {
                    this.addDemoMsg("bot", "Hi! 👋 I'm a live demo of the AMS AI platform.");
                }, 1000);
            }
        },

        closeDemo() {
            this.getEl("demo-panel")?.classList.remove("open");

            this.demoChatOpen = false;
        },

        toggleDemo() {
            this.demoChatOpen ? this.closeDemo() : this.openDemo();
        },

        saveLead() {
            const nameInput = this.getEl("dlf-name");
            const emailInput = this.getEl("dlf-email");

            const name = nameInput?.value?.trim();
            const email = emailInput?.value?.trim();

            if (!name || !email) return;

            const leadForm = this.getEl("demo-lead-form");

            if (!leadForm) return;

            leadForm.innerHTML = `
        <div style="text-align:center;padding:8px;font-size:12px;color:var(--green);font-weight:500">
          ✓ Got it, ${name}!
        </div>
      `;

            this.addDemoMsg("bot", `Perfect! I've saved your inquiry, ${name}.`);
        },

        // ── WHITE GLOVE ────────────────────────────────
        initWhiteGloveAnimation() {
            const observer = new IntersectionObserver(
                (entries) => {
                    if (!entries[0].isIntersecting) return;

                    observer.disconnect();

                    gsap.to(["#pb1", "#pb2", "#pb3", "#pb4"], {
                        opacity: 1,
                        duration: 0.4,
                        ease: "power2.out",
                        stagger: 0.18,
                        delay: 0.2,
                    });
                },
                {threshold: 0.25},
            );

            const sec = this.$el?.querySelector(".wg-section");

            if (sec) observer.observe(sec);
        },
        calcUpdate() {
            this.visitors = Number(this.visitors) || 500;
            this.avgRevenue = Number(this.avgRevenue) || 4500;
        },
    },
};
</script>
<template>
    <div id="home-landing">
        <!-- NAV -->
        <nav>
            <div class="nav-logo">
                <img src="/assets/images/logo.png" alt="AMS AI Logo" width="150" />
            </div>
            <div class="nav-right">
                <router-link to="/login" class="btn-text">Log in</router-link>
                <router-link to="/register" class="btn-outline">Register</router-link>
            </div>
        </nav>

        <!-- HERO -->
        <div class="hero">
            <div class="hero-content">
                <div class="hero-grid"></div>
                <div class="hero-glow"></div>
                <div class="hero-eyebrow">Introducing AMS AI</div>
                <h1 class="hero-title">From first conversation to <em>enrolled student</em> — one platform</h1>
                <p class="hero-sub">
                    AMS AI manages the entire student enrollment journey for schools and recruiters. AI chatbot, lead pipeline, follow-up workflows, and enrollment tracking — all in one integrated
                    flow.
                </p>
                <div class="hero-ctas">
                    <button class="btn-hp" @click="scrollToCta">
                        <svg width="15" height="15" viewBox="0 0 15 15" fill="none" stroke="white" stroke-width="1.8">
                            <rect x="1" y="3" width="13" height="10" rx="1.5" />
                            <path d="M1 6h13M4.5 3V1.5M10.5 3V1.5" />
                        </svg>
                        Book a Live Demo
                    </button>
                    <router-link to="/register" class="btn-hg">Create Account ↗</router-link>
                </div>
            </div>
            <div class="hero-mascot">
                <div class="journey-pills">
                    <div class="jp lit jp-tilt-1"><PhChatCircleDots class="jp-icon" :size="14" weight="duotone" />Conversation</div>
                    <div class="jp-arr">→</div>
                    <div class="jp lit jp-tilt-2"><PhTarget class="jp-icon" :size="14" weight="duotone" />Lead Captured</div>
                    <div class="jp-arr">→</div>
                    <div class="jp lit jp-tilt-3"><PhClipboardText class="jp-icon" :size="14" weight="duotone" />Follow-up</div>
                    <div class="jp-arr">→</div>
                    <div class="jp lit jp-tilt-4"><PhCheckCircle class="jp-icon" :size="14" weight="duotone" />Enrolled</div>
                </div>
                <div class="preview-wrap">
                    <div class="browser">
                        <div class="browser-bar">
                            <div class="b-dot" style="background: #ff5f56"></div>
                            <div class="b-dot" style="background: #ffbd2e"></div>
                            <div class="b-dot" style="background: #27c93f"></div>
                            <div class="burl"><span>app.amsai.io — ABC School International</span></div>
                        </div>
                        <div class="bb">
                            <div class="bbc">
                                <div class="bbl">Live Chatbot</div>
                                <div class="chat-wrap" id="hero-chat">
                                    <div class="chat-bot">
                                        <div class="chat-av">O</div>
                                        <div class="chat-bub">Hi! I'm ABC School's AI assistant. Ask me about programs, pricing, or how to apply. 👋</div>
                                    </div>
                                </div>
                            </div>
                            <div class="bbc">
                                <div class="bbl">Lead Pipeline</div>
                                <div id="hero-leads">
                                    <div class="lead-card lc-hot" id="lc1">
                                        <div class="lc-av"><img src="/assets/images/human/female/female-3.png" alt="Maria Santos" /></div>
                                        <div>
                                            <div class="lc-name">Maria Santos <span class="lc-badge bh">Hot</span></div>
                                            <div class="lc-meta">🇧🇷 Brazil · IELTS</div>
                                        </div>
                                        <div class="lc-score" style="color: var(--red)">91</div>
                                    </div>
                                    <div class="lead-card lc-warm" id="lc2">
                                        <div class="lc-av"><img src="/assets/images/human/male/avatar-8.png" alt="Carlos Ruiz" /></div>
                                        <div>
                                            <div class="lc-name">Carlos Ruiz <span class="lc-badge bw">Warm</span></div>
                                            <div class="lc-meta">🇲🇽 Mexico · Intensive</div>
                                        </div>
                                        <div class="lc-score" style="color: var(--amber)">74</div>
                                    </div>
                                    <div class="lead-card lc-cold" id="lc3">
                                        <div class="lc-av"><img src="/assets/images/human/female/female-8.png" alt="Yuki Tanaka" /></div>
                                        <div>
                                            <div class="lc-name">Yuki Tanaka</div>
                                            <div class="lc-meta">🇯🇵 Japan · Business</div>
                                        </div>
                                        <div class="lc-score" style="color: var(--blue)">58</div>
                                    </div>
                                    <div style="text-align: center; margin-top: 6px; font-size: 10px; color: var(--t3)">+ 309 more this month</div>
                                </div>
                            </div>
                            <div class="bbc">
                                <div class="bbl">Enrollment Pipeline</div>
                                <div class="ps">
                                    <div class="ps-head"><span class="ps-lbl">Contacted</span><span class="ps-cnt">34</span></div>
                                    <div class="ps-card"><span class="ps-flag">🇧🇷</span>Maria Santos<span style="margin-left: auto; font-size: 9px; color: var(--t3)">2d ago</span></div>
                                    <div class="ps-card"><span class="ps-flag">🇯🇵</span>Kenji Mori<span style="margin-left: auto; font-size: 9px; color: var(--t3)">3d ago</span></div>
                                </div>
                                <div class="ps">
                                    <div class="ps-head"><span class="ps-lbl">Proposal Sent</span><span class="ps-cnt">9</span></div>
                                    <div class="ps-card"><span class="ps-flag">🇲🇽</span>Ana García<span style="margin-left: auto; font-size: 9px; color: var(--t3)">1d ago</span></div>
                                </div>
                                <div class="ps">
                                    <div class="ps-head">
                                        <span class="ps-lbl" style="color: var(--green)">Enrolled ✓</span><span class="ps-cnt" style="color: var(--green); font-weight: 600">19</span>
                                    </div>
                                    <div class="ps-card" style="background: var(--greenl); border-color: rgba(29, 158, 117, 0.2)">
                                        <span class="ps-flag">🇰🇷</span><span style="color: var(--green); font-weight: 500">Ji-Yeon Park</span
                                        ><span style="margin-left: auto; font-size: 9px; color: var(--green)">Today</span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <!-- STATS -->
        <div class="stats-bar">
            <div class="si">
                <div class="si-num"><span class="counter" data-target="500">0</span><span class="si-unit">+</span></div>
                <div class="si-label">Schools in our ecosystem</div>
            </div>
            <div class="si">
                <div class="si-num"><span class="counter" data-target="70">0</span><span class="si-unit">+</span></div>
                <div class="si-label">Countries in our network</div>
            </div>
            <div class="si">
                <div class="si-num"><span class="counter" data-target="16">0</span></div>
                <div class="si-label">Years of industry experience</div>
            </div>
            <div class="si">
                <div class="si-num" style="font-size: 22px; padding-top: 4px">ISO</div>
                <div class="si-label">27001 & 9001 Certified</div>
            </div>
            <div class="si">
                <div class="si-num" style="font-size: 22px; padding-top: 4px">GDPR</div>
                <div class="si-label">Data Protection</div>
            </div>
        </div>

        <!-- WHO -->
        <div class="section reveal" style="background: var(--surface); border-bottom: 1px solid var(--border)">
            <div class="section-inner">
                <div class="sec-tag">Who it's for</div>
                <h2 class="sec-title">Built for every player in<br /><em>international education</em></h2>
                <div class="who-grid">
                    <article v-for="card in whoCards" :key="card.id" :class="['wcard', card.cardClass]">
                        <div :class="['wc-tag', card.tagClass]">{{ card.tag }}</div>
                        <h3 class="wc-title">{{ card.title }}</h3>
                        <p class="wc-sub">{{ card.sub }}</p>
                        <div class="wc-items">
                            <div v-for="item in card.items" :key="item" class="wi">
                                <div :class="['wi-dot', card.dotClass]">
                                    <PhCheck :size="10" weight="bold" />
                                </div>
                                <span>{{ item }}</span>
                            </div>
                        </div>
                    </article>
                </div>
            </div>
        </div>

        <!-- CALCULATOR -->
        <div class="section reveal">
            <div class="section-inner">
                <div class="sec-tag">ROI Calculator</div>
                <h2 class="sec-title">How many students are you<br /><em>missing</em> right now?</h2>
                <p class="sec-sub">Students visit your website at midnight, on weekends, from every time zone. Without an AI assistant, most leave without a trace.</p>
                <div class="calc-wrap">
                    <div class="calc-header">
                        <div>
                            <div class="calc-title">Student Loss Calculator</div>
                            <div class="calc-sub">Enter your monthly website traffic — see the students slipping through</div>
                        </div>
                        <div
                            style="
                                display: flex;
                                align-items: center;
                                gap: 6px;
                                padding: 6px 12px;
                                background: var(--redl);
                                border-radius: 8px;
                                font-size: 12px;
                                color: var(--red);
                                font-weight: 500;
                                white-space: nowrap;
                            "
                        >
                            <svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="currentColor" stroke-width="1.6">
                                <path d="M7 13s-5.5-3-5.5-7.5a5.5 5.5 0 0111 0C12.5 10 7 13 7 13z" />
                            </svg>
                            Live estimate
                        </div>
                    </div>
                    <div class="calc-body">
                        <div class="calc-inputs">
                            <label class="c-label">Monthly website visitors</label>
                            <div class="c-sublabel">How many people visit your website per month?</div>
                            <input type="range" class="c-slider" id="visitors" min="500" max="50000" step="500" v-model.number="visitors" @input="calcUpdate" />
                            <div class="c-val" id="visitors-val">{{ visitors.toLocaleString() }} visitors/month</div>

                            <label class="c-label">Average revenue per enrollment (USD)</label>
                            <div class="c-sublabel">Enter the average revenue generated from one successful enrollment</div>
                            <input type="number" class="c-revenue-input" id="avg-revenue" min="500" max="99999" step="100" v-model.number="avgRevenue" @input="calcUpdate" placeholder="e.g. 4500" />

                            <div style="margin-top: 20px; padding: 12px; background: var(--bluel); border-radius: 8px; font-size: 12px; color: var(--blue); line-height: 1.6">
                                <strong>How we calculate:</strong> With an advanced AI chatbot, web engagement typically reaches <strong>10%</strong> (compared to the 3% industry average for basic
                                tools). Of those engaged visitors, <strong>28%</strong> share contact details, and <strong>16%</strong> of those enroll.
                            </div>
                        </div>
                        <div class="calc-results">
                            <div class="result-highlight">
                                <div class="rh-val" id="res-missed">{{ calcData.missed.toLocaleString() }}</div>
                                <div class="rh-label">potential students missed per month</div>
                                <div class="rh-note">Students who visited, had questions, but left without engaging</div>
                            </div>
                            <div class="result-grid">
                                <div class="result-card">
                                    <div class="rc-val" id="res-convs" style="color: var(--blue)">{{ calcData.convs.toLocaleString() }}</div>
                                    <div class="rc-label">Potential conversations/mo</div>
                                </div>
                                <div class="result-card">
                                    <div class="rc-val" id="res-leads" style="color: var(--amber)">{{ calcData.leads.toLocaleString() }}</div>
                                    <div class="rc-label">Potential leads/mo</div>
                                </div>
                                <div class="result-card">
                                    <div class="rc-val" id="res-enr" style="color: var(--green)">{{ calcData.enr.toLocaleString() }}</div>
                                    <div class="rc-label">Potential enrollments/mo</div>
                                </div>
                                <div class="result-card">
                                    <div class="rc-val" id="res-rev" style="color: var(--blue)">{{ formatCurrency(calcData.rev) }}</div>
                                    <div class="rc-label">Potential Revenue/mo</div>
                                </div>
                            </div>
                            <button class="calc-cta" @click="scrollToCta">Stop losing these students — Book a Demo →</button>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <!-- FEATURES -->
        <div class="section reveal" style="background: var(--surface); border-top: 1px solid var(--border); border-bottom: 1px solid var(--border)">
            <div class="section-inner">
                <div class="sec-tag">Platform features</div>
                <h2 class="sec-title">Everything connected<br /><em>&amp; centralized</em></h2>
                <div class="feat-grid">
                    <div class="fcard">
                        <div class="fc-icon" style="background: var(--bluel)">
                            <svg viewBox="0 0 20 20" fill="none" stroke="#185FA5" stroke-width="1.6"><path d="M18 13a2 2 0 01-2 2H5l-3 3V4a2 2 0 012-2h12a2 2 0 012 2v9z" /></svg>
                        </div>
                        <div class="fc-title">AI Chatbot</div>
                        <div class="fc-desc">Embeds on your website and answers students using live data ensuring accurate prices, programs and availability.</div>
                        <div class="fc-items">
                            <div class="fi">Syncs to your GDS account (language schools)</div>
                            <div class="fi">Customizable widget — brand, name, avatar</div>
                            <div class="fi">Conversation starters & smart routing</div>
                            <div class="fi">Works across all devices worldwide</div>
                        </div>
                    </div>
                    <div class="fcard">
                        <div class="fc-icon" style="background: var(--redl)">
                            <svg viewBox="0 0 20 20" fill="none" stroke="#C0392B" stroke-width="1.6">
                                <path d="M10 17s-7-4-7-9a7 7 0 0114 0c0 5-7 9-7 9z" />
                                <circle cx="10" cy="8" r="2" fill="#C0392B" />
                            </svg>
                        </div>
                        <div class="fc-title">Lead Management</div>
                        <div class="fc-desc">Every conversation becomes a trackable lead. AI scores leads 0–100 to help prioritize attention and urgency.</div>
                        <div class="fc-items">
                            <div class="fi">Leads divided into Hot / Warm / Cold</div>
                            <div class="fi">AI generated chat summaries</div>
                            <div class="fi">Follow-up logging & call notes</div>
                            <div class="fi">Full chat history on every lead</div>
                        </div>
                    </div>
                    <div class="fcard">
                        <div class="fc-icon" style="background: var(--greenl)">
                            <svg viewBox="0 0 20 20" fill="none" stroke="#1D9E75" stroke-width="1.6">
                                <path d="M3 15l4-6 3 2.5 4-6 3 3" />
                                <rect x="1" y="1" width="18" height="18" rx="2" stroke-width="1.4" />
                            </svg>
                        </div>
                        <div class="fc-title">Analytics Dashboard</div>
                        <div class="fc-desc">Real-time funnel from first conversation to enrolled student. Country maps, peak-hour heatmaps, enrollment trends.</div>
                        <div class="fc-items">
                            <div class="fi">Conversation → Lead → Enrolled funnel</div>
                            <div class="fi">Country-based lead source map</div>
                            <div class="fi">Peak activity by day & hour</div>
                            <div class="fi">Monthly enrollment trend tracking</div>
                        </div>
                    </div>
                    <div class="fcard">
                        <div class="fc-icon" style="background: var(--amberl)">
                            <svg viewBox="0 0 20 20" fill="none" stroke="#B87C12" stroke-width="1.6">
                                <path d="M4 4h12a1 1 0 011 1v9a1 1 0 01-1 1H4a1 1 0 01-1-1V5a1 1 0 011-1z" />
                                <path d="M3 7h14M8 11h4" />
                            </svg>
                        </div>
                        <div class="fc-title">GDS Integration (Language Schools)</div>
                        <div class="fc-desc">
                            The Global Distribution System (GDS), connects your Language Schools and programs directly to a global network of premier international education professionals.
                        </div>
                        <div class="fc-items">
                            <div class="fi">Populate GDS directly from PDFs</div>
                            <div class="fi">GDS pricing database prevents AI hallucinations</div>
                            <div class="fi">Distribute your pricing globally</div>
                            <div class="fi">Missing field detection & alerts</div>
                        </div>
                    </div>
                    <div class="fcard">
                        <div class="fc-icon" style="background: var(--bluel)">
                            <svg viewBox="0 0 20 20" fill="none" stroke="#185FA5" stroke-width="1.6">
                                <path d="M17 10H7M10 7l-3 3 3 3" />
                                <circle cx="5" cy="10" r="1.5" fill="#185FA5" />
                            </svg>
                        </div>
                        <div class="fc-title">Enrollment Management</div>
                        <div class="fc-desc">Track lead conversion and enrollment progression for all students in one place.</div>
                        <div class="fc-items">
                            <div class="fi">Application sent via AMS → school confirms</div>
                            <div class="fi">Acceptance letter uploaded in-platform</div>
                            <div class="fi">Agency tracks status in real time</div>
                            <div class="fi">Student panel for end-to-end visibility</div>
                        </div>
                    </div>
                    <div class="fcard">
                        <div class="fc-icon" style="background: var(--s2)">
                            <svg viewBox="0 0 20 20" fill="none" stroke="#6B6860" stroke-width="1.6">
                                <rect x="3" y="3" width="14" height="14" rx="2" />
                                <path d="M3 8h14M8 8v9" />
                            </svg>
                        </div>
                        <div class="fc-title">Knowledge Base</div>
                        <div class="fc-desc">Upload any document — visa guides, FAQs, accommodation info — and the chatbot uses it to answer questions accurately.</div>
                        <div class="fc-items">
                            <div class="fi">Upload PDFs, URLs, or free text</div>
                            <div class="fi">School & branch-level content</div>
                            <div class="fi">Content expiry alerts & health tracking</div>
                            <div class="fi">Feeds chatbot, agency portal & student panel</div>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <!-- HOW IT WORKS -->
        <div class="section reveal">
            <div class="section-inner">
                <div class="sec-tag">Getting started</div>
                <h2 class="sec-title">Live on your website<br />in <em>under an hour</em></h2>
                <div class="how-grid">
                    <div class="hstep">
                        <div class="hs-num">01</div>
                        <div class="hs-title">Leave your details</div>
                        <div class="hs-desc">
                            Our AI Architects will reach out to discuss your specific goals, show you the platform in action, and outline a customized setup strategy for your institution.
                        </div>
                    </div>
                    <div class="hstep">
                        <div class="hs-num">02</div>
                        <div class="hs-title">Customize your platform</div>
                        <div class="hs-desc">
                            Brand your chatbot in the Widget Playground. Add Knowledge Base documents. Set up lead qualification questions. Configure your team's follow-up workflow.
                        </div>
                    </div>
                    <div class="hstep">
                        <div class="hs-num">03</div>
                        <div class="hs-title">Seamless integration in seconds</div>
                        <div class="hs-desc">
                            Paste a single &lt;script&gt; tag into your website and you’re done. Your AI is instantly live—capturing leads, scoring intent, and routing high-value students all the way
                            to enrollments.
                        </div>
                    </div>
                </div>
            </div>
        </div>
        <!-- WHITE-GLOVE SETUP -->
        <div class="wg-section reveal">
            <div class="wg-inner">
                <div class="wg-left">
                    <div class="wg-eyebrow">
                        <svg width="13" height="13" viewBox="0 0 13 13" fill="none" stroke="currentColor" stroke-width="1.5">
                            <path d="M6.5 1.5L2 4.5v5c0 1.4 2 2.5 4.5 2.5s4.5-1.1 4.5-2.5v-5L6.5 1.5z" />
                            <path d="M4.5 6.5l1.5 1.5 2.5-3" stroke-width="1.6" /></svg
                        >Managed Services
                    </div>
                    <h2 class="wg-title">White-Glove Setup &amp;<br />Custom Training.</h2>
                    <p class="wg-sub">
                        Don't have the time to configure your AI? Our dedicated <strong>Knowledge Base Architects</strong> will audit your institution's resources, structure your data, and
                        custom-train your AI agents for you — from day one.
                    </p>
                    <button class="wg-cta" @click="scrollToCta">
                        Explore Setup Packages <svg width="13" height="13" viewBox="0 0 13 13" fill="none" stroke="currentColor" stroke-width="2"><path d="M2.5 6.5h8M7 3l3.5 3.5L7 10" /></svg>
                    </button>
                </div>
                <div class="wg-right">
                    <div class="wg-cards">
                        <article class="wg-card">
                            <div class="wg-card-icon"><PhSparkle :size="16" weight="duotone" /></div>
                            <h3 class="wg-card-title">No-code onboarding</h3>
                            <p class="wg-card-desc">We handle setup end-to-end so your team can go live without technical overhead.</p>
                        </article>
                        <article class="wg-card">
                            <div class="wg-card-icon"><PhCheckCircle :size="16" weight="duotone" /></div>
                            <h3 class="wg-card-title">Zero-hallucination QA</h3>
                            <p class="wg-card-desc">Your assistants are tested against real scenarios before launch for reliable answers.</p>
                        </article>
                        <article class="wg-card">
                            <div class="wg-card-icon"><PhTarget :size="16" weight="duotone" /></div>
                            <h3 class="wg-card-title">Goal-based optimization</h3>
                            <p class="wg-card-desc">We tune flows around enrollments, lead quality, and conversion outcomes.</p>
                        </article>
                        <article class="wg-card">
                            <div class="wg-card-icon"><PhGlobeHemisphereWest :size="16" weight="duotone" /></div>
                            <h3 class="wg-card-title">Dedicated support</h3>
                            <p class="wg-card-desc">A dedicated account manager keeps your setup healthy as your organization scales.</p>
                        </article>
                    </div>
                </div>
            </div>
        </div>

        <!-- CTA -->
        <div class="cta-section reveal">
            <div class="cta-inner">
                <h2 class="cta-title">See AMS AI with<br />your <em>own data</em></h2>
                <p class="cta-sub">30-minute demo — we'll show you the full platform connected to your actual programs and prices.</p>
                <iframe
                    src="https://calendly.com/ams-help?embed_domain=ams4you.com&amp;embed_type=Inline"
                    width="100%"
                    height="730"
                    frameborder="0"
                    title="Select a Date &amp; Time - Calendly"
                    class="frame-calculate"
                    style="border: 1px solid var(--border2); border-radius: 1rem"
                ></iframe>
            </div>
        </div>

        <footer class="landing-footer">
            <div class="foot-left">
                <img src="/assets/images/logo.png" alt="AMS AI Studio" class="foot-logo" width="250" style="margin-bottom: 1rem" />
                <div class="foot-lead">
                    All rights reserved.<br />
                    Empowering global enrollment through intelligent education technology.
                </div>
                <button class="foot-cta" @click="scrollToCta">Learn more</button>

                <!-- <div class="foot-social">
                    <a href="https://www.instagram.com" target="_blank" rel="noopener noreferrer"><PhInstagramLogo :size="20" weight="duotone" />Instagram</a>
                    <a href="https://x.com" target="_blank" rel="noopener noreferrer"><PhXLogo :size="20" weight="duotone" />X</a>
                    <a href="https://www.linkedin.com" target="_blank" rel="noopener noreferrer"><PhLinkedinLogo :size="20" weight="duotone" />LinkedIn</a>
                </div> -->
            </div>
            <!-- <div class="foot-right">
                <div class="foot-pages-title">Pages</div>
                <div class="foot-links">
                    <router-link to="/">Home</router-link>
                    <a href="#">Project</a>
                    <a href="#">Solution</a>
                    <a href="#">Careers</a>
                    <a href="#">News</a>
                    <a href="#">About</a>
                </div>
                <div class="foot-copy">© 2026 AMS AI</div>
            </div> -->
        </footer>

        <!-- FLOATING DEMO CHATBOT -->
        <div class="demo-widget" id="demo-widget">
            <div class="demo-panel" id="demo-panel">
                <div class="demo-head">
                    <div class="demo-head-av"><img src="/assets/images/human/user.webp" alt="AMS AI assistant" /></div>
                    <div>
                        <div class="demo-head-name">AMS AI Demo</div>
                        <div class="demo-head-status">Online · Ask anything</div>
                    </div>
                    <button class="demo-close" @click="closeDemo">✕</button>
                </div>
                <div class="demo-msgs" id="demo-msgs"></div>
                <div class="demo-lead-form" id="demo-lead-form">
                    <div class="dlf-title">💡 Save your inquiry — get a full demo</div>
                    <input class="dlf-input" type="text" id="dlf-name" placeholder="Your name" />
                    <input class="dlf-input" type="email" id="dlf-email" placeholder="Work email" />
                    <button class="dlf-btn" @click="saveLead">Get Free Demo →</button>
                </div>
                <div class="demo-input-row">
                    <input class="demo-input" id="demo-input" type="text" placeholder="Ask about programs, pricing, applying..." @keydown.enter="sendDemo" />
                    <button class="demo-send" @click="sendDemo">
                        <svg viewBox="0 0 16 16" fill="none" stroke="white" stroke-width="2">
                            <line x1="14" y1="2" x2="7" y2="9" />
                            <polygon points="14 2 10 14 7 9 2 6 14 2" fill="white" stroke="none" />
                        </svg>
                    </button>
                </div>
                <div class="demo-powered">Powered by <strong>AMS AI</strong> · This is a live demo</div>
            </div>
        </div>
    </div>
</template>
